// Kept DOM-free: this module is shared with the server-side test program,
// so the liveness types live here and `api.ts` re-exports them.
export type LivenessMode = "off" | "monitor" | "enforce";
export interface LivenessTrialMeta {
  mode: LivenessMode;
  effectiveMode: LivenessMode;
  clamped?: true;
  ok?: boolean;
  reason?: string;
  teePubkeyHash?: string;
}

export type LivenessDisplayState = "not-run" | "passed" | "failed-not-blocking" | "failed-blocking";
export const LIVENESS_REASON_LABELS: Record<string, string> = {
  liveness_absent: "Check not done", liveness_malformed: "Unreadable result",
  liveness_tee_key: "Untrusted device key", liveness_challenge_mismatch: "Result from another sign-in",
  liveness_verdict: "Check failed", liveness_signature: "Result not signed correctly",
  liveness_attestation_replayed: "Result already used",
};

const LEGACY_REASON_PREFIX = "liveness required: ";
const MODES = ["off", "monitor", "enforce"] as const;
const isMode = (v: unknown): v is LivenessMode => typeof v === "string" && (MODES as readonly string[]).includes(v);

/**
 * Strictly validate a `metadata.liveness` value. Rows without a boolean `ok`
 * are not trials: that covers malformed historical data as well as the
 * clamp-drift telemetry rows the server emits without running a check.
 */
function parseTrialMeta(value: unknown): LivenessTrialMeta | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const v = value as Record<string, unknown>;
  if (typeof v.ok !== "boolean" || !isMode(v.mode) || !isMode(v.effectiveMode)) return undefined;
  return {
    mode: v.mode,
    effectiveMode: v.effectiveMode,
    ...(v.clamped === true ? { clamped: true as const } : {}),
    ok: v.ok,
    ...(typeof v.reason === "string" ? { reason: v.reason } : {}),
    ...(typeof v.teePubkeyHash === "string" ? { teePubkeyHash: v.teePubkeyHash } : {}),
  };
}

export function classifyLiveness(event: { action: string; metadata: Record<string, unknown> | null | undefined }): {
  liveness: LivenessTrialMeta | undefined;
  state: LivenessDisplayState;
} {
  const meta = event.metadata ?? {};
  const structured = parseTrialMeta(meta.liveness);
  const reason = typeof meta.reason === "string" ? meta.reason : undefined;
  // Rejections recorded before verification.failed carried structured
  // liveness metadata only have the audit reason string.
  const legacyReason = !structured && event.action === "verification.failed" && reason?.startsWith(LEGACY_REASON_PREFIX)
    ? reason.slice(LEGACY_REASON_PREFIX.length)
    : undefined;
  const liveness = structured
    ?? (legacyReason ? { mode: "enforce", effectiveMode: "enforce", ok: false, reason: legacyReason } satisfies LivenessTrialMeta : undefined);
  const state: LivenessDisplayState = !liveness
    ? "not-run"
    : liveness.ok
      ? "passed"
      : event.action === "verification.failed" ? "failed-blocking" : "failed-not-blocking";
  return { liveness, state };
}
